/**
 * Shared helper for pages that fetch live cluster resources.
 * Handles: no cluster selected, provisioned cluster, loading, error states.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useCluster } from '../context/ClusterContext';
import { useNavigate } from 'react-router-dom';
import { clustersAPI } from '../services/api';
import './ResourcePage.css';

// ── Generic cluster-aware page wrapper ────────────────────────────────────────
// `deps` (optional): extra values that, when changed, should trigger a refetch — e.g. the
// selected namespace on the heavy list pages. These are DISCRETE changes (a dropdown), not
// per-keystroke, so including them in `load` is safe and does NOT reintroduce the
// reload-on-every-character bug (which is about the inline fetchFn identity, still guarded
// by fetchRef). Search-box text stays OUT of deps → typing never refetches.
// `waitFor` (optional): when provided and falsy, the fetch is DEFERRED — don't hit the API
// until it becomes truthy. The namespace pages pass `waitFor={ns}` so the list does NOT fire
// an expensive all-namespaces (`-A`) query on first render before the namespace is known
// (that was the ~8.7s ConfigMaps load: the initial ns='' fetched -A, then a second scoped
// fetch ran once ns resolved). Pages without a selector omit it → fetch immediately as before.
export function LivePage({ title, fetchFn, children, deps = [], waitFor = undefined }) {
  const { selectedCluster } = useCluster();
  const navigate = useNavigate();
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  // Keep the latest fetchFn in a ref so `load` stays stable across parent
  // re-renders (e.g. a child search box typing into local state). Without this,
  // the inline `fetchFn={id => api.getX(id)}` prop is a new reference every
  // render → load() re-runs → the whole subtree unmounts/refetches on each
  // keystroke, which was the "screen reloads on every character" bug.
  const fetchRef = useRef(fetchFn);
  useEffect(() => { fetchRef.current = fetchFn; }, [fetchFn]);

  const clusterId    = selectedCluster?.id;
  const clusterReady = selectedCluster?.is_imported;

  const load = useCallback(async () => {
    if (!clusterReady) return;
    // Defer until `waitFor` resolves (namespace pages): don't fire the -A query before the
    // namespace is chosen. `undefined` = no gate (non-namespace pages fetch immediately).
    if (waitFor !== undefined && !waitFor) return;
    setLoading(true); setError(null);
    try {
      const res = await fetchRef.current(clusterId);
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || `Failed to fetch ${title}`);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clusterId, clusterReady, title, waitFor, ...deps]);

  // Only refetch when the selected cluster actually changes, not on every
  // parent render.
  useEffect(() => { load(); }, [load]);

  if (!selectedCluster) return (
    <div className="resource-page">
      <div className="page-header"><h1>{title}</h1></div>
      <div className="no-cluster-msg">
        No cluster selected.{' '}
        <span className="link" onClick={() => navigate('/clusters')}>Add or select a cluster</span>
        {' '}to view {title.toLowerCase()}.
      </div>
    </div>
  );

  if (!selectedCluster.is_imported) return (
    <div className="resource-page">
      <div className="page-header"><h1>{title} — {selectedCluster.name}</h1></div>
      <div className="no-cluster-msg">
        Live resource viewing is available for imported clusters only.
      </div>
    </div>
  );

  return (
    <div className="resource-page">
      <div className="page-header">
        <div>
          <h1>{title}</h1>
          <div className="resource-cluster-badge">{selectedCluster.name}</div>
        </div>
        <button className="btn btn-secondary" onClick={load} disabled={loading}>
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>
      {error && <div className="resource-error">{error}</div>}
      {/* Initial load only: show the full loading message when we have no data yet. */}
      {loading && !data && <div className="resource-loading">Loading {title.toLowerCase()}...</div>}
      {/* Keep the table (and its search box) mounted during refetch so typing a
          search query no longer unmounts the page. A subsequent refresh updates
          `data` in place without discarding the rendered children. */}
      {data && children(data, load, selectedCluster.id)}
    </div>
  );
}

// ── Namespace selector (server-side scoping for heavy list pages) ───────────────
// The heavy pages (ConfigMaps/Secrets/Deployments/Workloads) used to fetch ALL
// namespaces (`kubectl get -A`, ~1280 items, ~10s). This hook fetches the cluster's
// namespace list ONCE (from the already-existing /resources endpoint) and gives the page
// a selected namespace to pass into its fetch, so the server query is scoped to one
// namespace (sub-second). Default selection = "default" if present, else the first ns.
export function useNamespaces(clusterId) {
  const [namespaces, setNamespaces] = useState([]);
  const [ns, setNs] = useState('');
  // Tracks whether the namespace list fetch has settled (success OR failure) — distinct
  // from `ns` being falsy, which is ALSO true while genuinely resolving. Without this,
  // every page consuming this hook had a real race: `ns` starts as '' (meaning "unknown
  // yet"), but a page's own fetch effect reads that same '' as "fetch with no namespace
  // filter" and fires immediately — sending a slow, unfiltered ALL-NAMESPACES query (the
  // same expensive-when-unfiltered shape as the config-resources/storage/workloads
  // latency issue fixed earlier) a beat before the real namespace-scoped request lands a
  // moment later. Confirmed live via Network tab: two /jobs/paged requests fired on one
  // page load, the first with no `namespace=` param at all. Callers should gate their
  // fetch on `resolved` (or `namespaces.length > 0`), not just truthy `ns`.
  const [resolved, setResolved] = useState(false);
  useEffect(() => {
    let cancelled = false;
    if (!clusterId) return;
    setResolved(false);
    // Dedicated endpoint rather than getResources(), which fetches the entire cluster
    // snapshot (~240KB with 1272 pods) to read a list of names. Falls back to it for
    // non-Rancher clusters, which have no Steve-backed namespace listing.
    clustersAPI.listNamespaceNames(clusterId)
      .catch(() => clustersAPI.getResources(clusterId))
      .then(res => {
        if (cancelled) return;
        const list = (res.data?.namespaces || [])
          .map(n => (typeof n === 'string' ? n : n.name))
          .filter(Boolean)
          .sort();
        setNamespaces(list);
        // default to 'default' if it exists, else the first namespace
        setNs(prev => prev || (list.includes('default') ? 'default' : (list[0] || '')));
      })
      .catch(() => { /* namespace list unavailable → page still works with empty selector */ })
      .finally(() => { if (!cancelled) setResolved(true); });
    return () => { cancelled = true; };
  }, [clusterId]);
  return { namespaces, ns, setNs, resolved };
}

// Dropdown control for the namespace selector, styled to sit in a FilterBar.
export function NamespaceSelect({ namespaces, ns, onChange }) {
  return (
    <select value={ns} onChange={e => onChange(e.target.value)} title="Namespace">
      {namespaces.length === 0 && <option value="">(loading namespaces…)</option>}
      {namespaces.map(n => <option key={n} value={n}>{n}</option>)}
    </select>
  );
}

// ── Shared table helpers ───────────────────────────────────────────────────────
export function FilterBar({ children, count, total, label }) {
  return (
    <div className="resource-filter">
      {children}
      <span className="resource-count">
        {count !== total ? `${count} / ${total}` : count} {label}
      </span>
    </div>
  );
}

export function DataTable({ cols, rows, emptyMsg = 'No items found' }) {
  return (
    <div className="table-card">
      <table className="ds">
        <thead><tr>{cols.map(c => <th key={c}>{c}</th>)}</tr></thead>
        <tbody>
          {rows.length === 0
            ? <tr><td colSpan={cols.length} style={{ textAlign: 'center', padding: 32, color: 'var(--muted)' }}>{emptyMsg}</td></tr>
            : rows
          }
        </tbody>
      </table>
    </div>
  );
}

export function StatusBadge({ status }) {
  const cls =
    ['Active', 'Running', 'Bound', 'Available'].includes(status) ? 'status-active' :
    ['Pending'].includes(status)                                   ? 'status-pending' :
    'status-failed';
  return <span className={`status-badge ${cls}`}>{status}</span>;
}

export function Age({ ts }) {
  if (!ts) return <span style={{ color: 'var(--muted)' }}>N/A</span>;
  return <span style={{ color: 'var(--muted)', fontSize: 12 }}>{new Date(ts).toLocaleDateString()}</span>;
}

export function Code({ children }) {
  return <code style={{ fontSize: 12 }}>{children}</code>;
}

// ── kubectl exec result helpers ───────────────────────────────────────────────
// clustersAPI.exec() returns HTTP 200 even when the underlying kubectl command
// FAILS — the failure is on stderr / a non-zero exit_code, not an HTTP error.
// The delete/create handlers used to only catch network exceptions, so a failed
// delete (RBAC denied, read-only agent, etc.) fell through to refresh() and the
// user saw a phantom "success" — the real "delete not working" bug. Use these to
// inspect the actual command result.

// Combined stdout+stderr text from an exec response (either shape the API uses).
export function execOutput(res) {
  const out = res?.data?.output || res?.data?.stdout || '';
  const err = res?.data?.stderr || '';
  return `${out}\n${err}`.trim();
}

// True if a mutating kubectl command actually succeeded. A delete is a success
// when kubectl reports "<kind> ... deleted" OR exit_code 0 with no error text.
// Note: callers should NOT pass --ignore-not-found, otherwise deleting a
// non-existent object reports success silently.
export function execOk(res, { verbs = ['deleted', 'created', 'configured', 'unchanged', 'replaced'] } = {}) {
  const text = execOutput(res).toLowerCase();
  const code = res?.data?.exit_code;
  const hasError = /error|forbidden|denied|not found|unable to|invalid|refused/.test(text);
  if (hasError) return false;
  if (verbs.some(v => text.includes(v))) return true;
  return code === 0;
}

// Throw a human-readable Error if the exec result indicates failure — lets a
// handler's catch block surface the real cluster error to the user.
export function assertExecOk(res, action = 'Operation') {
  if (!execOk(res)) {
    const text = execOutput(res);
    throw new Error(text || `${action} failed (no output from cluster — is the cluster agent connected with write scope?)`);
  }
}

// ── Re-export clustersAPI for convenience ─────────────────────────────────────
export { clustersAPI };
